import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

export interface Project {
  id: string;
  name: string;
  created_at: string;
  sheet_id?: string | null;
  allowed_users: string[];
}

interface ProjectContextType {
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const STORAGE_KEY = 'fintitles_selected_project';

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedProject, setSelectedProjectState] = useState<Project | null>(() => {
    // Initialize from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Wrapper function that updates both state and localStorage
  const setSelectedProject = useCallback((project: Project | null): void => {
    setSelectedProjectState(project);
    
    if (project) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(project));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const value = useMemo(
    () => ({ selectedProject, setSelectedProject }),
    [selectedProject, setSelectedProject]
  );

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};


